import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ChartBarIcon,
  PhoneIcon,
  ClockIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarDaysIcon,
  TagIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import toast from 'react-hot-toast';
import AnalyticsSplit from './analytics/AnalyticsSplit';
import { isPostventaHiddenForTenant } from '../config/permissions';

const PIE_COLORS = ['#22c55e', '#ef4444', '#f59e0b', '#3b82f6', '#8b5cf6', '#ec4899', '#6b7280'];

const formatDuration = (seconds) => {
  const safe = Number.isFinite(seconds) ? Math.max(0, Math.round(seconds)) : 0;
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}m ${s}s`;
};

const numberSafe = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

// Bucket descriptors for the sales KPI drill-downs. outcomeKeys mirror the backend
// classification in get_analytics_overview so bucket count == drill-down rows.
const SALES_DRILLDOWN_BUCKETS = {
  appointments_scheduled: { labelKey: 'analytics.appointments', outcomeKeys: ['appointment_scheduled', 'appointment_booked'] },
  test_drives_scheduled: { labelKey: 'analytics.testDrives', outcomeKeys: ['test_drive_scheduled'] },
  interested_leads: { labelKey: 'analytics.interestedLeads', outcomeKeys: ['interested', 'appointment_scheduled', 'appointment_booked', 'test_drive_scheduled'] },
  callbacks_requested: { labelKey: 'analytics.callbacksRequested', outcomeKeys: ['callback_requested', 'callback_scheduled', 'callback'] },
  not_interested: { labelKey: 'analytics.notInterested', outcomeKeys: ['not_interested', 'refused', 'rejected', 'declined'] },
};

const DISPOSITION_OPTIONS = ['interested', 'appointment_scheduled', 'test_drive_scheduled', 'callback_requested', 'not_interested'];

// Drill-down window start. MUST match the backend overview window exactly, or the
// drill-down row count will drift from the KPI card count at the day boundary. The
// backend computes range_start = UTC-midnight(today) - (days - 1), so compute the
// same UTC-midnight anchor here (NOT local midnight) to keep card == rows.
const rangeStartDate = (timeRange) => {
  const days = { '7d': 7, '30d': 30, '90d': 90 }[timeRange] || 30;
  const now = new Date();
  const utcMidnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const d = new Date(utcMidnight);
  d.setUTCDate(d.getUTCDate() - (days - 1));
  return d.toISOString();
};

// Generic drill-down: LIST ↔ DETAIL ↔ disposition stepper. Oldest-first. Saving a
// disposition re-fetches overview (buckets overlap — never optimistic ±1) and removes
// the dispositioned lead from the live list.
function DrillDown({ bucketKey, timeRange, tenantId, onClose, onDispositioned }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const bucket = SALES_DRILLDOWN_BUCKETS[bucketKey];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list' | 'detail'
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/analytics/call-outcomes', {
        params: {
          outcomes: bucket.outcomeKeys.join(','),
          start_date: rangeStartDate(timeRange),
          // Scope the drill-down to the SAME tenant as the clicked KPI card. Without
          // this, a super_admin / cross-tenant view aggregates every tenant's outcomes
          // while the card counts one tenant (card=1 vs modal=16). null/undefined =>
          // own-tenant single-tenant path (param omitted).
          ...(tenantId ? { tenant_id: tenantId } : {}),
          limit: 2000,
        },
      });
      setRows(res.data?.items || []);
    } catch (e) {
      toast.error(t('analytics.drilldownError', 'Failed to load records'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [bucket, timeRange, tenantId, t]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (row) => {
    setActive(row);
    setDetail(null);
    setView('detail');
    if (row.call_sid) {
      try {
        const res = await api.get(`/calls/${row.call_sid}/detail`);
        setDetail(res.data || null);
      } catch (e) {
        setDetail(null);
      }
    }
  };

  // "Ver más": navigate to where this lead lives — the campaign detail page with the
  // lead row focused/highlighted. Mirrors the notification deep-link in Layout.js
  // (navigate(`/campaigns/{campaignId}?focusContact={contactId}` | `?focusPhone={phone}`)).
  // Records with no campaign (e.g. postventa/direct calls) can't deep-link, so the
  // button is only rendered when a campaign_id is present.
  const canViewMore = !!active?.campaign_id;
  const viewMore = () => {
    if (!active?.campaign_id) return;
    const qs = new URLSearchParams();
    if (active.contact_id) qs.set('focusContact', active.contact_id);
    else if (active.phone) qs.set('focusPhone', active.phone);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    if (onClose) onClose(); // close the modal on navigate
    navigate(`/campaigns/${active.campaign_id}${suffix}`);
  };

  const disposition = async (outcome) => {
    if (!active?.campaign_id || !active?.contact_id) {
      toast.error(t('analytics.dispositionNoContact', 'This record has no linked contact'));
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/analytics/contacts/${active.contact_id}/outcome`, null, {
        params: { campaign_id: active.campaign_id, outcome },
      });
      toast.success(t('analytics.dispositionSaved', 'Saved'));
      // Remove from live list and auto-advance to next (or back to list).
      const remaining = rows.filter((r) => r !== active);
      setRows(remaining);
      if (onDispositioned) onDispositioned(); // re-fetch overview (counts refresh)
      if (remaining.length > 0) {
        openDetail(remaining[0]);
      } else {
        setView('list');
        setActive(null);
      }
    } catch (e) {
      toast.error(t('analytics.dispositionError', 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-card w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-lg shadow-xl border border-border" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h3 className="text-base font-semibold text-foreground">
            {t(bucket.labelKey)}
            {view === 'list' && <span className="ml-2 text-sm" style={{ color: 'var(--muted)' }}>({rows.length})</span>}
          </h3>
          <button type="button" onClick={onClose} className="text-sm" style={{ color: 'var(--muted)' }}>✕</button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>
        ) : view === 'list' ? (
          rows.length === 0 ? (
            <div className="flex items-center justify-center h-40" style={{ color: 'var(--muted)' }}>{t('analytics.noData')}</div>
          ) : (
            <ul className="divide-y divide-border">
              {rows.map((row, i) => (
                <li key={row.outcome_id || row.call_sid || i}>
                  <button type="button" onClick={() => openDetail(row)} className="w-full text-left px-5 py-3 hover:bg-background flex items-center justify-between gap-3">
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-foreground truncate">{row.customer_name || row.phone || t('analytics.unknownLead', 'Unknown lead')}</span>
                      <span className="block text-xs" style={{ color: 'var(--muted)' }}>{(row.outcome || '').replace(/_/g, ' ')}{row.created_at ? ` · ${new Date(row.created_at).toLocaleDateString()}` : ''}</span>
                    </span>
                    <span className="text-xs shrink-0" style={{ color: 'var(--muted)' }}>{t('analytics.drilldownView', 'View')} ›</span>
                  </button>
                </li>
              ))}
            </ul>
          )
        ) : (
          <div className="px-5 py-4 space-y-4">
            <button type="button" onClick={() => { setView('list'); setActive(null); }} className="text-xs" style={{ color: 'var(--muted)' }}>‹ {t('analytics.drilldownBack', 'Back to list')}</button>
            <div>
              <p className="text-sm font-semibold text-foreground">{active?.customer_name || active?.phone || t('analytics.unknownLead', 'Unknown lead')}</p>
              {active?.phone && <p className="text-xs" style={{ color: 'var(--muted)' }}>{active.phone}</p>}
              {active?.campaign_name && <p className="text-xs" style={{ color: 'var(--muted)' }}>{active.campaign_name}</p>}
            </div>
            {detail?.summary && (
              <div className="text-sm text-foreground bg-background rounded-md p-3 whitespace-pre-wrap">{detail.summary}</div>
            )}
            {canViewMore && (
              <button
                type="button"
                onClick={viewMore}
                className="px-3 py-1.5 text-xs font-medium rounded-md bg-card text-foreground border border-border hover:bg-background"
              >
                {t('analytics.viewMore', 'Ver más')} ›
              </button>
            )}
            <div>
              <p className="text-xs font-medium mb-2" style={{ color: 'var(--muted)' }}>{t('analytics.setDisposition', 'Set disposition')}</p>
              <div className="flex flex-wrap gap-2">
                {DISPOSITION_OPTIONS.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    disabled={saving}
                    onClick={() => disposition(opt)}
                    className="px-3 py-1.5 text-xs font-medium rounded-full bg-card text-foreground border border-border hover:bg-primary hover:text-white disabled:opacity-50"
                  >
                    {t(`analytics.disposition_${opt}`, opt.replace(/_/g, ' '))}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Legacy campaign-type analytics (general / sales / cobranza KPI cards + drill-downs).
// Rendered ONLY when the "Campañas" sub-tab is active so its two full-table scans
// (perf oct10: ~6-9 s) never run for the Postventa / Ventas tabs.
function AnalyticsCampaigns() {
  const { t } = useTranslation();
  const { tenant, user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const isAdmin = currentUser?.role === 'admin';
  const _layoutMap = { general: 'all', sales: 'sales', cobranza: 'collection' };
  const tenantCampaignType = _layoutMap[tenant?.analytics_layout] || 'all';

  // Cross-tenant: super_admin and admin always see the tenant tab strip;
  // managers with >1 managed_tenants also get it.
  const managedTenants = currentUser?.managed_tenants || [];
  // isMultiTenant: managers with >1 assigned tenant, plus admin and super_admin
  // who always get the cross-tenant tab strip backed by the /cross-tenant-overview endpoint.
  const isMultiTenant = managedTenants.length > 1 || isSuperAdmin || isAdmin;
  // selectedTenantId: null = own tenant (single-tenant path), string = specific managed tenant
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  // crossTenantData: {tenants: [{tenant_id, business_name, analytics}]}
  const [crossTenantData, setCrossTenantData] = useState(null);

  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [timeRange, setTimeRange] = useState('30d');
  const [campaignType, setCampaignType] = useState(tenantCampaignType); // all | sales | collection
  const [scope, setScope] = useState('org'); // 'org' | 'mine'

  // Holds the AbortController for the in-flight fetch so scope/filter changes cancel stale requests
  const abortControllerRef = useRef(null);

  // Drill-down: which sales KPI bucket is open (null = closed)
  const [drillBucket, setDrillBucket] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    // Cancel any previous in-flight request to prevent last-write-wins races
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true);
      setFetchError(false);
      if (isMultiTenant) {
        // Fetch cross-tenant overview; derive per-tenant analytics from it
        const res = await api.get('/analytics/cross-tenant-overview', { params: { time_range: timeRange, campaign_type: campaignType }, signal: controller.signal });
        if (controller.signal.aborted) return;
        const data = res.data || { tenants: [] };
        setCrossTenantData(data);
        // Show data for the selected tenant (default to first)
        const firstId = selectedTenantId || (data.tenants[0]?.tenant_id ?? null);
        if (!selectedTenantId && firstId) setSelectedTenantId(firstId);
        const match = data.tenants.find(t => t.tenant_id === (selectedTenantId || firstId));
        setAnalyticsData(match?.analytics || null);
      } else {
        const overviewRes = await api.get('/analytics/overview', { params: { time_range: timeRange, campaign_type: campaignType, scope }, signal: controller.signal });
        if (controller.signal.aborted) return;
        setAnalyticsData(overviewRes.data || null);
      }
    } catch (error) {
      // Ignore cancellation errors — these are intentional when scope/filter changes mid-flight
      if (error?.name === 'CanceledError' || error?.name === 'AbortError' || controller.signal.aborted) {
        console.debug('[Analytics] fetch cancelled (filter/scope changed mid-flight)');
        return;
      }
      setFetchError(true);
      console.error('[Analytics] Error fetching analytics:', error);
    } finally {
      // Only clear loading if this request wasn't superseded by a newer one
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [timeRange, campaignType, scope, isMultiTenant, selectedTenantId]);

  // When user switches tenant tab, extract from already-fetched crossTenantData (no re-fetch)
  const handleTenantSelect = useCallback((tenantId) => {
    setSelectedTenantId(tenantId);
    if (crossTenantData) {
      const match = crossTenantData.tenants.find(t => t.tenant_id === tenantId);
      setAnalyticsData(match?.analytics || null);
    }
  }, [crossTenantData]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const autoSuggestedType = useMemo(() => {
    if (!analyticsData?.campaign_type_summary) return 'all';
    const { sales = 0, collection = 0 } = analyticsData.campaign_type_summary;
    if (sales > 0 && collection === 0) return 'sales';
    if (collection > 0 && sales === 0) return 'collection';
    return 'all';
  }, [analyticsData]);

  const outcomesPieData = (analyticsData?.calls_by_outcome || []).map((row, index) => ({
    ...row,
    color: PIE_COLORS[index % PIE_COLORS.length],
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <p className="text-base font-medium" style={{ color: 'var(--muted)' }}>
          {t('analytics.serviceUnavailable', 'Analytics service unavailable')}
        </p>
        <button
          type="button"
          onClick={fetchAnalytics}
          className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-white hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary"
        >
          {t('analytics.retry', 'Retry')}
        </button>
      </div>
    );
  }

  const salesMetrics = analyticsData?.sales_metrics || {};
  const collectionMetrics = analyticsData?.collection_metrics || {};
  const openDrill = (key) => setDrillBucket(key);

  return (
    <div className="space-y-6 overflow-hidden">
      {/* Cross-tenant tenant tab strip — only visible for multi-tenant managers */}
      {isMultiTenant && crossTenantData && (
        <div className="flex flex-wrap gap-2 border-b border-border pb-3">
          {crossTenantData.tenants.map((t) => (
            <button
              key={t.tenant_id}
              type="button"
              onClick={() => handleTenantSelect(t.tenant_id)}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
                selectedTenantId === t.tenant_id
                  ? 'bg-primary text-white'
                  : 'bg-card text-foreground border border-border hover:bg-background'
              }`}
            >
              {t.business_name || t.tenant_id}
              {t.error && <span className="ml-1 text-xs text-red-400">⚠</span>}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-xl font-bold leading-7 text-foreground sm:text-2xl truncate">{t('analytics.title')}</h2>
          <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>
            {t('analytics.subtitle', 'Analytics by campaign type for business ROI')}
          </p>
          {autoSuggestedType !== 'all' && (
            <p className="mt-1 text-xs" style={{ color: 'var(--muted)' }}>
              {t('analytics.suggestedView', 'Suggested view')}: <strong>{autoSuggestedType === 'sales' ? t('analytics.sales', 'Sales') : t('analytics.cobranza', 'Cobranza')}</strong>
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-3 shrink-0">
          {isSuperAdmin && (
          <div className="inline-flex rounded-full shadow-sm">
            <button
              type="button"
              onClick={() => setCampaignType('all')}
              className={`px-4 py-2 text-sm font-medium rounded-l-full ${campaignType === 'all' ? 'bg-primary text-white' : 'bg-card text-foreground border border-border hover:bg-background'}`}
            >
              {t('analytics.general', 'General')}
            </button>
            <button
              type="button"
              onClick={() => setCampaignType('sales')}
              className={`px-4 py-2 text-sm font-medium ${campaignType === 'sales' ? 'bg-primary text-white' : 'bg-card text-foreground border border-border hover:bg-background'}`}
            >
              {t('analytics.sales', 'Sales')}
            </button>
            <button
              type="button"
              onClick={() => setCampaignType('collection')}
              className={`px-4 py-2 text-sm font-medium rounded-r-full ${campaignType === 'collection' ? 'bg-primary text-white' : 'bg-card text-foreground border border-border hover:bg-background'}`}
            >
              {t('analytics.cobranza')}
            </button>
          </div>
          )}

          {/* Scope: Org vs Mine — hidden when cross-tenant tab strip is active */}
          {!isSuperAdmin && !isMultiTenant && (
            <div className="inline-flex rounded-full shadow-sm">
              <button
                type="button"
                onClick={() => setScope('org')}
                className={`px-4 py-2 text-sm font-medium rounded-l-full ${scope === 'org' ? 'bg-primary text-white' : 'bg-card text-foreground border border-border hover:bg-background'}`}
              >
                {t('analytics.scopeOrg', 'Organización')}
              </button>
              <button
                type="button"
                onClick={() => setScope('mine')}
                className={`px-4 py-2 text-sm font-medium rounded-r-full ${scope === 'mine' ? 'bg-primary text-white' : 'bg-card text-foreground border border-border hover:bg-background'}`}
              >
                {t('analytics.scopeMine', 'Mías')}
              </button>
            </div>
          )}

          <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} className="input-field w-auto">
            <option value="7d">{t('analytics.last7days', 'Last 7 days')}</option>
            <option value="30d">{t('analytics.last30days', 'Last 30 days')}</option>
            <option value="90d">{t('analytics.last90days', 'Last 90 days')}</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4">
        <div className="card"><div className="flex items-center"><div className="flex-shrink-0 hidden sm:block"><PhoneIcon className="h-8 w-8 text-primary-600" /></div><div className="sm:ml-4 w-full sm:w-0 sm:flex-1"><dt className="text-xs sm:text-sm font-medium leading-tight" style={{ color: 'var(--muted)' }}>{t('analytics.callsToday')}</dt><dd className="text-lg sm:text-xl font-semibold text-foreground">{numberSafe(analyticsData?.totals?.today)}</dd></div></div></div>
        <div className="card"><div className="flex items-center"><div className="flex-shrink-0 hidden sm:block"><ChartBarIcon className="h-8 w-8 text-success-600" /></div><div className="sm:ml-4 w-full sm:w-0 sm:flex-1"><dt className="text-xs sm:text-sm font-medium leading-tight" style={{ color: 'var(--muted)' }}>{t('analytics.callsThisWeek')}</dt><dd className="text-lg sm:text-xl font-semibold text-foreground">{numberSafe(analyticsData?.totals?.week)}</dd></div></div></div>
        <div className="card"><div className="flex items-center"><div className="flex-shrink-0 hidden sm:block"><CheckCircleIcon className="h-8 w-8 text-warning-600" /></div><div className="sm:ml-4 w-full sm:w-0 sm:flex-1"><dt className="text-xs sm:text-sm font-medium leading-tight" style={{ color: 'var(--muted)' }}>{t('analytics.callsThisMonth')}</dt><dd className="text-lg sm:text-xl font-semibold text-foreground">{numberSafe(analyticsData?.totals?.month)}</dd></div></div></div>
        <div className="card"><div className="flex items-center"><div className="flex-shrink-0 hidden sm:block"><ClockIcon className="h-8 w-8 text-danger-600" /></div><div className="sm:ml-4 w-full sm:w-0 sm:flex-1"><dt className="text-xs sm:text-sm font-medium leading-tight" style={{ color: 'var(--muted)' }}>{t('analytics.avgDuration')}</dt><dd className="text-lg sm:text-xl font-semibold text-foreground">{formatDuration(analyticsData?.avg_call_duration_seconds || 0)}</dd></div></div></div>
      </div>

      {campaignType === 'sales' && (
        <>
          {/* KPI Row — 5 clickable cards (open lead drill-down) */}
          <div className="grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-6">
            <button type="button" onClick={() => openDrill('appointments_scheduled')} className="card text-left w-full hover:ring-2 hover:ring-primary cursor-pointer"><div className="flex items-center"><div className="flex-shrink-0 hidden sm:block"><CalendarDaysIcon className="h-6 w-6 text-blue-600" /></div><div className="sm:ml-4 min-w-0"><dt className="text-xs sm:text-sm leading-tight line-clamp-2 min-h-8" style={{ color: 'var(--muted)' }}>{t('analytics.appointments')}</dt><dd className="text-lg sm:text-xl font-semibold text-foreground">{numberSafe(salesMetrics.appointments_scheduled)}</dd></div></div></button>
            <button type="button" onClick={() => openDrill('test_drives_scheduled')} className="card text-left w-full hover:ring-2 hover:ring-primary cursor-pointer"><div className="flex items-center"><div className="flex-shrink-0 hidden sm:block"><TagIcon className="h-6 w-6 text-indigo-600" /></div><div className="sm:ml-4 min-w-0"><dt className="text-xs sm:text-sm leading-tight line-clamp-2 min-h-8" style={{ color: 'var(--muted)' }}>{t('analytics.testDrives')}</dt><dd className="text-lg sm:text-xl font-semibold text-foreground">{numberSafe(salesMetrics.test_drives_scheduled)}</dd></div></div></button>
            <button type="button" onClick={() => openDrill('interested_leads')} className="card text-left w-full hover:ring-2 hover:ring-primary cursor-pointer"><div className="flex items-center"><div className="flex-shrink-0 hidden sm:block"><UserGroupIcon className="h-6 w-6 text-emerald-600" /></div><div className="sm:ml-4 min-w-0"><dt className="text-xs sm:text-sm leading-tight line-clamp-2 min-h-8" style={{ color: 'var(--muted)' }}>{t('analytics.interestedLeads')}</dt><dd className="text-lg sm:text-xl font-semibold text-foreground">{numberSafe(salesMetrics.interested_leads)}</dd></div></div></button>
            <button type="button" onClick={() => openDrill('callbacks_requested')} className="card text-left w-full hover:ring-2 hover:ring-primary cursor-pointer"><div className="flex items-center"><div className="flex-shrink-0 hidden sm:block"><PhoneIcon className="h-6 w-6 text-amber-600" /></div><div className="sm:ml-4 min-w-0"><dt className="text-xs sm:text-sm leading-tight line-clamp-2 min-h-8" style={{ color: 'var(--muted)' }}>{t('analytics.callbacksRequested')}</dt><dd className="text-lg sm:text-xl font-semibold text-foreground">{numberSafe(salesMetrics.callbacks_requested)}</dd></div></div></button>
            <button type="button" onClick={() => openDrill('not_interested')} className="card text-left w-full hover:ring-2 hover:ring-primary cursor-pointer"><div className="flex items-center"><div className="flex-shrink-0 hidden sm:block"><XCircleIcon className="h-6 w-6 text-red-600" /></div><div className="sm:ml-4 min-w-0"><dt className="text-xs sm:text-sm leading-tight line-clamp-2 min-h-8" style={{ color: 'var(--muted)' }}>{t('analytics.notInterested')}</dt><dd className="text-lg sm:text-xl font-semibold text-foreground">{numberSafe(salesMetrics.not_interested)}</dd></div></div></button>
          </div>

          {/* Row 2 — Embudo + Llamadas por Día + Horas Pico (3 columnas) */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="card min-w-0">
              <h3 className="text-lg font-medium text-foreground mb-4">{t('analytics.salesFunnel')}</h3>
              <div className="h-56 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      { etapa: t('analytics.funnelStages.calls', 'Llamadas'), valor: numberSafe(salesMetrics?.funnel?.calls) },
                      { etapa: t('analytics.funnelStages.interested', 'Interesados'), valor: numberSafe(salesMetrics?.funnel?.interested) },
                      { etapa: t('analytics.funnelStages.appointments', 'Cita'), valor: numberSafe(salesMetrics?.funnel?.appointments) },
                      { etapa: t('analytics.funnelStages.testDrives', 'Test Drive'), valor: numberSafe(salesMetrics?.funnel?.test_drives) },
                    ]}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="etapa" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 12 }} width={40} />
                    <Tooltip />
                    <Bar dataKey="valor" fill="#2563eb" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="card min-w-0">
              <h3 className="text-lg font-medium text-foreground mb-4">{t('analytics.callsByDay')}</h3>
              <div className="h-56 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analyticsData?.calls_per_day || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 12 }} width={40} />
                    <Tooltip />
                    <Line type="monotone" dataKey="calls" stroke="#3b82f6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="card min-w-0">
              <h3 className="text-lg font-medium text-foreground mb-4">{t('analytics.peakHours')}</h3>
              <div className="h-56 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analyticsData?.peak_call_hours || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={2} />
                    <YAxis tick={{ fontSize: 12 }} width={40} />
                    <Tooltip />
                    <Bar dataKey="calls" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Row 3 — Resultado de llamadas (pie) + Por campaña (bar) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card min-w-0">
              <h3 className="text-lg font-medium text-foreground mb-4">{t('analytics.callsByOutcome')}</h3>
              <div className="h-56 sm:h-64">
                {(outcomesPieData || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={outcomesPieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={70}
                        dataKey="count"
                      >
                        {outcomesPieData.map((entry, index) => (
                          <Cell key={`cell-sales-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full" style={{ color: 'var(--muted)' }}>{t('analytics.noData')}</div>
                )}
              </div>
            </div>

            <div className="card min-w-0">
              <h3 className="text-lg font-medium text-foreground mb-4">{t('analytics.callsByCampaign')}</h3>
              <div className="h-56 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analyticsData?.calls_by_campaign || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="campaign_name" tick={{ fontSize: 11 }} interval={0} angle={-10} height={60} />
                    <YAxis tick={{ fontSize: 12 }} width={40} />
                    <Tooltip />
                    <Bar dataKey="calls" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      )}

      {campaignType === 'collection' && (
        <>
          {/* 4-KPI row: cobranza-specific metrics (volume KPIs today/week/month/avgDuration are in the shared base row above) */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="card"><div className="flex items-center"><div className="flex-shrink-0 hidden sm:block"><CurrencyDollarIcon className="h-7 w-7 text-emerald-600" /></div><div className="sm:ml-3 w-full sm:w-0 sm:flex-1"><dt className="text-xs font-medium truncate" style={{ color: 'var(--muted)' }}>{t('analytics.paymentPromises')}</dt><dd className="text-xl font-semibold text-foreground">{numberSafe(collectionMetrics.payment_promises)}</dd></div></div></div>
            <div className="card"><div className="flex items-center"><div className="flex-shrink-0 hidden sm:block"><PhoneIcon className="h-7 w-7 text-blue-500" /></div><div className="sm:ml-3 w-full sm:w-0 sm:flex-1"><dt className="text-xs font-medium truncate" style={{ color: 'var(--muted)' }}>{t('analytics.callbacksScheduled')}</dt><dd className="text-xl font-semibold text-foreground">{numberSafe(collectionMetrics.callbacks_scheduled)}</dd></div></div></div>
            <div className="card"><div className="flex items-center"><div className="flex-shrink-0 hidden sm:block"><CheckCircleIcon className="h-7 w-7 text-amber-500" /></div><div className="sm:ml-3 w-full sm:w-0 sm:flex-1"><dt className="text-xs font-medium truncate" style={{ color: 'var(--muted)' }}>{t('analytics.notReached')}</dt><dd className="text-xl font-semibold text-foreground">{numberSafe(collectionMetrics.not_reached)}</dd></div></div></div>
            <div className="card"><div className="flex items-center"><div className="flex-shrink-0 hidden sm:block"><ChartBarIcon className="h-7 w-7 text-success-600" /></div><div className="sm:ml-3 w-full sm:w-0 sm:flex-1"><dt className="text-xs font-medium truncate" style={{ color: 'var(--muted)' }}>{t('analytics.totalCalls')}</dt><dd className="text-xl font-semibold text-foreground">{numberSafe(collectionMetrics.total_calls)}</dd></div></div></div>
          </div>

          {/* 3-panel chart row for cobranza */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="card min-w-0">
              <h3 className="text-sm font-semibold text-foreground mb-3">{t('analytics.callsByHour')}</h3>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analyticsData?.peak_call_hours || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={3} />
                    <YAxis tick={{ fontSize: 11 }} width={32} />
                    <Tooltip />
                    <Bar dataKey="calls" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="card min-w-0">
              <h3 className="text-sm font-semibold text-foreground mb-3">{t('analytics.callsByDay')}</h3>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analyticsData?.calls_per_day || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} width={32} />
                    <Tooltip />
                    <Line type="monotone" dataKey="calls" stroke="#3b82f6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="card min-w-0">
              <h3 className="text-sm font-semibold text-foreground mb-3">{t('analytics.outcomeDistribution')}</h3>
              <div className="h-48">
                {(outcomesPieData || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={outcomesPieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={65}
                        dataKey="count"
                      >
                        {outcomesPieData.map((entry, index) => (
                          <Cell key={`cobranza-cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full" style={{ color: 'var(--muted)' }}>{t('common.noResults')}</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {campaignType === 'all' && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card min-w-0">
              <h3 className="text-lg font-medium text-foreground mb-4">{t('analytics.callsByDay')}</h3>
              <div className="h-56 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={analyticsData?.calls_per_day || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} width={40} />
                    <Tooltip />
                    <Line type="monotone" dataKey="calls" stroke="#3b82f6" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="card min-w-0">
              <h3 className="text-lg font-medium text-foreground mb-4">{t('analytics.peakHours')}</h3>
              <div className="h-56 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analyticsData?.peak_call_hours || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={2} />
                    <YAxis tick={{ fontSize: 12 }} width={40} />
                    <Tooltip />
                    <Bar dataKey="calls" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card min-w-0">
              <h3 className="text-lg font-medium text-foreground mb-4">{t('analytics.callsByOutcome')}</h3>
              <div className="h-56 sm:h-64">
                {(outcomesPieData || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={outcomesPieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={70}
                        dataKey="count"
                      >
                        {outcomesPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-full" style={{ color: 'var(--muted)' }}>{t('common.noResults')}</div>
                )}
              </div>
            </div>

            <div className="card min-w-0">
              <h3 className="text-lg font-medium text-foreground mb-4">{t('analytics.callsByCampaign')}</h3>
              <div className="h-56 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analyticsData?.calls_by_campaign || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="campaign_name" tick={{ fontSize: 11 }} interval={0} angle={-10} height={60} />
                    <YAxis tick={{ fontSize: 12 }} width={40} />
                    <Tooltip />
                    <Bar dataKey="calls" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      )}

      {drillBucket && (
        <DrillDown
          bucketKey={drillBucket}
          timeRange={timeRange}
          tenantId={isMultiTenant ? selectedTenantId : null}
          onClose={() => setDrillBucket(null)}
          onDispositioned={fetchAnalytics}
        />
      )}
    </div>
  );
}

// P1 oct24 (Roger 17:03 CST): ONE "Analítica" page with Postventa | Ventas sub-tabs
// (shared filters: rango de fechas · sucursal · asesor) + the legacy campaign view
// as a third tab. Default tab = Postventa unless the tenant layout hides postventa
// (cobranza tenants) -> Ventas. The tab persists in the URL (?tab=) so it can be
// deep-linked / bookmarked.
export function resolveDefaultTab(tenant) {
  return isPostventaHiddenForTenant(tenant) ? 'ventas' : 'postventa';
}

function Analytics() {
  const { tenant } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const tab = ['postventa', 'ventas', 'campanas'].includes(requested) ? requested : resolveDefaultTab(tenant);
  const onTabChange = (next) => {
    const qs = new URLSearchParams(searchParams);
    qs.set('tab', next);
    setSearchParams(qs, { replace: true });
  };
  return <AnalyticsSplit tab={tab} onTabChange={onTabChange} legacy={<AnalyticsCampaigns />} />;
}

export default Analytics;
